class AiChatBlock extends HTMLElement {
  #pendingQuestion = null;

  connectedCallback() {
    this.pills = this.querySelectorAll('.ai-chat-pill');
    this.drawer = document.getElementById(this.dataset.drawerId);
    this.widget = this.drawer?.querySelector('ai-chat-widget');

    this.pills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#pendingQuestion = e.currentTarget.dataset.question;
        if (this.drawer?.show) this.drawer.show();
      });
    });

    if (this.drawer) {
      this.drawer.addEventListener('dialog:after-show', () => {
        if (this.#pendingQuestion && this.widget) {
          this.widget.sendMessage(this.#pendingQuestion);
          this.#pendingQuestion = null;
        } else if (this.widget) {
          this.widget.focusInput();
        }
      });

      this.drawer.addEventListener('dialog:after-hide', () => {
        if (this.widget) this.widget.resetConversation();
      });
    }
  }
}

class AiChatWidget extends HTMLElement {
  #askedQuestions = new Set();
  #chatStarted = false;

  // Inline quiz state
  #quizActive = false;
  #quizStep = 0;
  #quizAnswers = {};
  #quizSelectedItems = new Set();
  #quizSessionId = null;

  #quizSteps = [
    {
      key: 'sport',
      question: "What do you LOVE doing?",
      subtitle: "We'll find the perfect bag for your passions.",
      type: 'single',
      options: ['Tennis', 'Padel', 'Pickleball', 'Adventure'],
      skipIf: null,
    },
    {
      key: 'racquets',
      question: "How many racquets do you carry?",
      type: 'single',
      options: ['1-2 Racquets', '3-4 Racquets', '5-6+ Racquets'],
      skipIf: (answers) => answers.sport === 'Adventure',
    },
    {
      key: 'items',
      question: "What does your bag need to fit?",
      subtitle: "Select as many as apply.",
      type: 'multi',
      options: ['Laptop', 'Tablet', 'Clothes', 'Shoes', 'Camera', 'Snacks'],
      skipIf: null,
    },
    {
      key: 'gender',
      question: "What's your gender?",
      type: 'single',
      options: ['Male', 'Female', 'Prefer not to say'],
      skipIf: null,
    },
    {
      key: 'age',
      question: "What is your age group?",
      type: 'single',
      options: ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
      skipIf: null,
    },
    {
      key: 'email',
      question: "You're almost done!",
      subtitle: "Drop your email for an exclusive gift with your first order.",
      type: 'email',
      skipIf: null,
    },
  ];

  connectedCallback() {
    this.apiEndpoint = this.dataset.apiEndpoint || 'https://cancha-ai-chat.cancha-chat.workers.dev/chat';
    this.baseUrl = this.apiEndpoint.replace(/\/(chat|quiz|subscribe)$/, '');
    this.quizEndpoint = this.baseUrl + '/quiz';
    this.productId = this.dataset.productId;
    this.productContext = this.#parseJson('[data-product-context]');
    this.suggestedQuestions = this.#parseJson('[data-suggested-questions]') || [];
    this.conversationHistory = [];
    this.isSending = false;

    this.messagesEl = this.querySelector('[data-messages]');
    this.formEl = this.querySelector('[data-form]');
    this.inputEl = this.querySelector('[data-input]');
    this.submitBtn = this.formEl?.querySelector('button[type="submit"]');
    this.suggestionsEl = this.querySelector('[data-suggestions]');
    this.quizCtaEl = this.querySelector('[data-quiz-cta]');
    this.emailFormEl = this.querySelector('[data-email-form]');
    this.emailInputEl = this.querySelector('[data-email-input]');
    this.emailSuccessEl = this.querySelector('[data-email-success]');
    this.emailSectionEl = this.querySelector('[data-email-section]');

    this.formEl?.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.inputEl?.value?.trim();
      if (text) this.sendMessage(text);
    });

    // Suggestion pills inside drawer
    this.suggestionsEl?.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-suggestion]');
      if (pill) {
        if (pill.dataset.suggestion.toLowerCase().includes('free gift')) {
          this.#startGiftCapture();
        } else {
          this.sendMessage(pill.dataset.suggestion);
        }
      }
    });

    // Quiz CTA button
    this.quizCtaEl?.addEventListener('click', (e) => {
      e.preventDefault();
      this.startQuiz();
    });

    // Email form
    this.emailFormEl?.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = this.emailInputEl?.value?.trim();
      if (email) this.#submitEmail(email);
    });
  }

  focusInput() {
    this.inputEl?.focus();
  }

  resetConversation() {
    this.conversationHistory = [];
    this.#askedQuestions.clear();
    this.#chatStarted = false;

    // Reset quiz state
    this.#quizActive = false;
    this.#quizStep = 0;
    this.#quizAnswers = {};
    this.#quizSelectedItems.clear();
    this.#quizSessionId = null;

    if (this.messagesEl) this.messagesEl.innerHTML = '';
    if (this.formEl) this.formEl.hidden = false;
    if (this.quizCtaEl) this.quizCtaEl.hidden = false;
    this.#showSuggestions();
    this.focusInput();
  }

  async sendMessage(text) {
    if (this.isSending || !text || this.#quizActive) return;
    this.isSending = true;
    this.#setSubmitDisabled(true);

    // Track asked question so its pill gets hidden
    this.#askedQuestions.add(text);

    if (!this.#chatStarted) {
      this.#chatStarted = true;
      this.#track('chat_started', { product_id: this.productId });
    }

    if (this.inputEl) this.inputEl.value = '';
    this.#appendMessage('user', text);
    this.conversationHistory.push({ role: 'user', content: text });
    this.#track('chat_message_sent', { product_id: this.productId });
    this.#hideSuggestions();

    const typingEl = this.#showTypingIndicator();

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          product_context: this.productContext,
          conversation_history: this.conversationHistory,
        }),
      });

      if (!response.ok) throw new Error('API error');

      const data = await response.json();
      typingEl?.remove();

      const reply = data.reply || 'Sorry, I could not generate a response.';
      this.#appendMessage('assistant', reply);
      this.conversationHistory.push({ role: 'assistant', content: reply });

      // Show Add to Cart button if the AI suggests it
      if (data.show_atc) {
        this.#renderAtcButton();
      }

      // Show remaining suggestions after response
      this.#showSuggestions();
    } catch (err) {
      console.error('AI Chat error:', err, 'Endpoint:', this.apiEndpoint);
      typingEl?.remove();
      this.#appendError();
      this.#showSuggestions();
    } finally {
      this.isSending = false;
      this.#setSubmitDisabled(false);
      this.focusInput();
    }
  }

  async #submitEmail(email) {
    const btn = this.emailFormEl?.querySelector('button');
    if (btn) btn.disabled = true;

    try {
      await fetch(this.baseUrl + '/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          product_id: this.productContext?.product_id,
          product_title: this.productContext?.product_title,
          product_url: this.productContext?.product_url,
        }),
      });

      if (this.emailFormEl) this.emailFormEl.hidden = true;
      if (this.emailSuccessEl) this.emailSuccessEl.hidden = false;
    } catch {
      if (btn) btn.disabled = false;
    }
  }

  // ---- Free Gift Email Capture ----

  #startGiftCapture() {
    this.#askedQuestions.add('free gift');
    this.#hideSuggestions();

    // Show user bubble
    this.#appendMessage('user', 'I want my free gift!');

    // Show bot response with inline email form
    const wrapper = document.createElement('div');
    wrapper.className = 'ai-chat-msg ai-chat-msg--assistant';

    const avatar = document.createElement('div');
    avatar.className = 'ai-chat-msg__avatar';
    avatar.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0283/7984/0586/files/compass-black-red_3f86301c-110d-468f-8baa-aba20dc9e862.png?v=1767804967" alt="Cancha" width="20" height="20" style="border-radius:50%;object-fit:contain;">';
    wrapper.appendChild(avatar);

    const bubble = document.createElement('div');
    bubble.className = 'ai-chat-msg__bubble';
    bubble.textContent = "We'd love to send you a gift with your first order! Drop your email below.";
    wrapper.appendChild(bubble);
    this.messagesEl?.appendChild(wrapper);

    // Inline email form
    const formWrap = document.createElement('div');
    formWrap.className = 'ai-chat-quiz-email';
    formWrap.style.paddingLeft = '36px';
    formWrap.innerHTML = `
      <form class="ai-chat-gift-email-form">
        <input type="email" placeholder="Enter your email address" required autocomplete="email" />
        <button type="submit">Submit</button>
      </form>
    `;
    this.messagesEl?.appendChild(formWrap);

    const form = formWrap.querySelector('form');
    const input = formWrap.querySelector('input');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = input.value.trim();
      if (!email) return;

      const btn = form.querySelector('button');
      btn.disabled = true;
      btn.textContent = 'Sending...';

      try {
        const baseUrl = this.apiEndpoint.replace(/\/(chat|quiz|subscribe)$/, '');
        await fetch(baseUrl + '/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            source: 'free_gift',
            product_id: this.productContext?.product_id,
            product_title: this.productContext?.product_title,
            product_url: this.productContext?.product_url,
          }),
        });

        formWrap.innerHTML = '';
        this.#appendMessage('assistant', "You're all set! Keep an eye on your inbox for your exclusive gift. 🎁");
      } catch {
        btn.disabled = false;
        btn.textContent = 'Submit';
        this.#appendMessage('assistant', 'Something went wrong — please try again.');
      }

      this.#showSuggestions();
      this.#scrollToBottom();
    });

    this.#scrollToBottom();
    input.focus();
  }

  // ---- Inline Quiz ----

  startQuiz() {
    if (this.#quizActive) return;
    this.#quizActive = true;
    this.#quizStep = 0;
    this.#quizAnswers = {};
    this.#quizSelectedItems.clear();
    this.#quizSessionId = null;

    // Hide chat UI, show quiz flow in messages
    this.#hideSuggestions();
    if (this.quizCtaEl) this.quizCtaEl.hidden = true;
    if (this.formEl) this.formEl.hidden = true;

    this.#track('quiz_started', { source: 'pdp_chat' });
    this.#quizAdvance(0);
  }

  #quizAdvance(index) {
    this.#quizStep = index;

    // Skip steps that should be skipped
    while (this.#quizStep < this.#quizSteps.length) {
      const step = this.#quizSteps[this.#quizStep];
      if (step.skipIf && step.skipIf(this.#quizAnswers)) {
        this.#quizStep++;
        continue;
      }
      break;
    }

    // Past last step — submit quiz
    if (this.#quizStep >= this.#quizSteps.length) {
      this.#quizSubmit();
      return;
    }

    const step = this.#quizSteps[this.#quizStep];
    const typingEl = this.#showTypingIndicator();

    setTimeout(() => {
      typingEl?.remove();
      this.#appendMessage('assistant', step.question);
      if (step.subtitle) {
        // Append subtitle as a smaller follow-up
        const sub = this.messagesEl?.lastElementChild?.querySelector('.ai-chat-bubble--assistant');
        if (sub) {
          const small = document.createElement('div');
          small.style.cssText = 'font-size:12px;opacity:0.6;margin-top:4px;';
          small.textContent = step.subtitle;
          sub.appendChild(small);
        }
      }

      switch (step.type) {
        case 'single':
          this.#quizRenderSingle(step);
          break;
        case 'multi':
          this.#quizRenderMulti(step);
          break;
        case 'email':
          this.#quizRenderEmail();
          break;
      }
      this.#scrollToBottom();
    }, 400);
  }

  #quizRenderSingle(step) {
    const container = document.createElement('div');
    container.className = 'ai-chat-quiz-options';

    step.options.forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ai-chat-quiz-option';
      btn.textContent = opt;
      btn.addEventListener('click', () => {
        this.#quizSelect(step.key, opt, container);
      });
      container.appendChild(btn);
    });

    this.messagesEl?.appendChild(container);
    this.#scrollToBottom();
  }

  #quizRenderMulti(step) {
    this.#quizSelectedItems.clear();
    const container = document.createElement('div');
    container.className = 'ai-chat-quiz-options ai-chat-quiz-options--multi';

    step.options.forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ai-chat-quiz-option';
      btn.textContent = opt;
      btn.addEventListener('click', () => {
        if (this.#quizSelectedItems.has(opt)) {
          this.#quizSelectedItems.delete(opt);
          btn.classList.remove('ai-chat-quiz-option--selected');
        } else {
          this.#quizSelectedItems.add(opt);
          btn.classList.add('ai-chat-quiz-option--selected');
        }
        continueBtn.disabled = this.#quizSelectedItems.size === 0;
      });
      container.appendChild(btn);
    });

    const continueBtn = document.createElement('button');
    continueBtn.type = 'button';
    continueBtn.className = 'ai-chat-quiz-continue';
    continueBtn.textContent = 'Continue';
    continueBtn.disabled = true;
    continueBtn.addEventListener('click', () => {
      const values = Array.from(this.#quizSelectedItems);
      continueBtn.remove();
      this.#quizSelect(step.key, values, container);
    });

    this.messagesEl?.appendChild(container);
    this.messagesEl?.appendChild(continueBtn);
    this.#scrollToBottom();
  }

  #quizRenderEmail() {
    const wrap = document.createElement('div');
    wrap.className = 'ai-chat-quiz-email';

    const form = document.createElement('form');
    const input = document.createElement('input');
    input.type = 'email';
    input.placeholder = 'Enter your email address';
    input.autocomplete = 'email';
    input.required = true;
    input.className = 'ai-chat-quiz-email__input';
    form.appendChild(input);

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'ai-chat-quiz-email__btn';
    submitBtn.textContent = 'Submit';
    form.appendChild(submitBtn);

    wrap.appendChild(form);

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'ai-chat-quiz-skip';
    skipBtn.textContent = 'Skip — see my results';
    wrap.appendChild(skipBtn);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = input.value.trim();
      if (email) {
        this.#quizAnswers.email = email;
        this.#track('quiz_email_submitted', { source: 'pdp_chat' });
        this.#appendMessage('user', email);
        this.#quizDisableEl(wrap);
        this.#quizAdvance(this.#quizStep + 1);
      }
    });

    skipBtn.addEventListener('click', () => {
      this.#track('quiz_email_skipped', { source: 'pdp_chat' });
      this.#appendMessage('user', 'Skipped');
      this.#quizDisableEl(wrap);
      this.#quizAdvance(this.#quizStep + 1);
    });

    this.messagesEl?.appendChild(wrap);
    this.#scrollToBottom();
    input.focus();
  }

  #quizSelect(key, value, optionsEl) {
    this.#quizAnswers[key] = value;
    const displayText = Array.isArray(value) ? value.join(', ') : value;
    this.#track('quiz_step_completed', { quiz_step: key, quiz_answer: displayText, source: 'pdp_chat' });
    this.#appendMessage('user', displayText);
    this.#quizDisableEl(optionsEl);
    this.#quizAdvance(this.#quizStep + 1);
  }

  #quizDisableEl(el) {
    if (!el) return;
    el.querySelectorAll('button, input').forEach((b) => {
      b.disabled = true;
      b.style.pointerEvents = 'none';
    });
    el.style.opacity = '0.5';
  }

  async #quizSubmit() {
    const typingEl = this.#showTypingIndicator();

    try {
      const response = await fetch(this.quizEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: {
            sport: this.#quizAnswers.sport,
            racquets: this.#quizAnswers.racquets,
            items: this.#quizAnswers.items,
            gender: this.#quizAnswers.gender,
            age: this.#quizAnswers.age,
          },
          email: this.#quizAnswers.email || null,
        }),
      });

      if (!response.ok) throw new Error('Quiz API error');

      const data = await response.json();
      typingEl?.remove();
      this.#quizSessionId = data.session_id;

      if (data.recommendations?.length > 0) {
        this.#track('quiz_completed', {
          source: 'pdp_chat',
          quiz_recommended: data.recommendations.map((r) => r.handle).join(','),
          quiz_sport: this.#quizAnswers.sport,
        });
        this.#appendMessage('assistant', "Here's your perfect match:");
        await this.#quizRenderRecommendation(data.recommendations[0]);
      } else {
        this.#appendMessage('assistant', "Sorry, I couldn't generate a recommendation. Try asking me directly!");
      }
    } catch (err) {
      console.error('Inline quiz error:', err);
      typingEl?.remove();
      this.#appendMessage('assistant', "Something went wrong. Try asking me directly instead!");
    } finally {
      // Restore chat UI
      this.#quizActive = false;
      if (this.formEl) this.formEl.hidden = false;
      if (this.suggestionsEl) this.suggestionsEl.hidden = false;
      if (this.quizCtaEl) this.quizCtaEl.hidden = false;
      this.focusInput();
      // Scroll after bottom UI is restored so the recommendation card stays visible
      setTimeout(() => this.#scrollToBottom(), 100);
    }
  }

  async #quizRenderRecommendation(rec) {
    const safeHandle = (rec.handle || '').replace(/[^a-z0-9\-]/gi, '');
    if (!safeHandle) return;

    const productUrl = `/products/${safeHandle}`;

    // Build the card immediately from API data (no async, no fetch)
    const card = document.createElement('div');
    card.className = 'ai-chat-quiz-rec';

    // Image from API
    if (rec.image) {
      const imgLink = document.createElement('a');
      imgLink.href = productUrl;
      imgLink.className = 'ai-chat-quiz-rec__img-link';
      const img = document.createElement('img');
      img.className = 'ai-chat-quiz-rec__img';
      img.src = rec.image;
      img.alt = rec.title || safeHandle;
      imgLink.appendChild(img);
      card.appendChild(imgLink);
    }

    const titleEl = document.createElement('div');
    titleEl.className = 'ai-chat-quiz-rec__title';
    titleEl.textContent = rec.title || safeHandle;
    card.appendChild(titleEl);

    const priceEl = document.createElement('div');
    priceEl.className = 'ai-chat-quiz-rec__price';
    priceEl.textContent = rec.price || '';
    card.appendChild(priceEl);

    if (rec.reason) {
      const reasonEl = document.createElement('div');
      reasonEl.className = 'ai-chat-quiz-rec__reason';
      reasonEl.textContent = rec.reason;
      card.appendChild(reasonEl);
    }

    // Shop Now button (immediate, works without Shopify data)
    const atcBtn = document.createElement('button');
    atcBtn.type = 'button';
    atcBtn.className = 'ai-chat-atc__btn';
    atcBtn.textContent = 'Shop Now';
    atcBtn.addEventListener('click', () => { window.location.href = productUrl; });
    card.appendChild(atcBtn);

    const link = document.createElement('a');
    link.className = 'ai-chat-quiz-rec__link';
    link.href = productUrl;
    link.textContent = 'View product details';
    card.appendChild(link);

    // Wrap card in assistant message layout so it renders like other messages
    const wrapper = document.createElement('div');
    wrapper.className = 'ai-chat-msg ai-chat-msg--assistant';
    wrapper.style.maxWidth = '100%';
    wrapper.appendChild(card);
    this.messagesEl?.appendChild(wrapper);
    this.#scrollToBottom();

    // Now try to enhance with live Shopify data (variants, ATC) in the background
    try {
      const res = await fetch(`${Shopify.routes.root}products/${encodeURIComponent(safeHandle)}.js`);
      if (!res.ok) return;
      const shopifyProduct = await res.json();

      const allVariants = shopifyProduct.variants || [];
      const availableVariants = allVariants.filter((v) => v.available);
      const firstAvailable = availableVariants[0] || allVariants[0];

      // Update image if Shopify has one
      if (shopifyProduct.featured_image) {
        const img = card.querySelector('.ai-chat-quiz-rec__img');
        if (img) img.src = shopifyProduct.featured_image;
      }

      // Update price from live data
      if (firstAvailable) {
        try {
          const formatted = Shopify.formatMoney ? Shopify.formatMoney(firstAvailable.price) : `$${(firstAvailable.price / 100).toFixed(2)}`;
          priceEl.textContent = formatted;
        } catch { /* keep API price */ }
      }

      // Variant selector
      let selectedVariant = firstAvailable;
      if (availableVariants.length > 1) {
        const selectWrap = document.createElement('div');
        selectWrap.className = 'ai-chat-quiz-rec__variants';
        const select = document.createElement('select');
        select.className = 'ai-chat-quiz-rec__variant-select';
        availableVariants.forEach((v) => {
          const opt = document.createElement('option');
          opt.value = v.id;
          opt.textContent = v.title;
          if (v === firstAvailable) opt.selected = true;
          select.appendChild(opt);
        });
        select.addEventListener('change', () => {
          selectedVariant = availableVariants.find((v) => String(v.id) === select.value) || firstAvailable;
          try {
            priceEl.textContent = Shopify.formatMoney ? Shopify.formatMoney(selectedVariant.price) : `$${(selectedVariant.price / 100).toFixed(2)}`;
          } catch { /* ignore */ }
        });
        selectWrap.appendChild(select);
        card.insertBefore(selectWrap, atcBtn);
      }

      // Upgrade button to Add to Cart if product is available
      if (firstAvailable?.available) {
        const newBtn = document.createElement('button');
        newBtn.type = 'button';
        newBtn.className = 'ai-chat-atc__btn';
        newBtn.textContent = 'Add to Cart';
        newBtn.addEventListener('click', () => this.#addToCart(newBtn, selectedVariant?.id || firstAvailable.id, 'quiz_pdp'));
        atcBtn.replaceWith(newBtn);
      }
    } catch { /* Shopify fetch failed — card already shows with API data */ }

    this.#scrollToBottom();
  }

  #renderAtcButton() {
    const firstVariant = this.productContext?.variants?.find((v) => v.available);
    if (!firstVariant) return;

    const hasComparePrice = firstVariant.compare_at_price && firstVariant.compare_at_price !== firstVariant.price;

    const container = document.createElement('div');
    container.className = 'ai-chat-atc';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ai-chat-atc__btn';

    if (hasComparePrice) {
      const strikethrough = document.createElement('s');
      strikethrough.textContent = firstVariant.compare_at_price;
      btn.append('Add to Cart \u2014 ', strikethrough, ' ', firstVariant.price);
    } else {
      btn.textContent = `Add to Cart \u2014 ${firstVariant.price}`;
    }

    btn.addEventListener('click', () => this.#addToCart(btn, firstVariant.id, 'chat'));
    container.appendChild(btn);

    this.messagesEl?.appendChild(container);
    this.#scrollToBottom();
  }

  #parseJson(selector) {
    const script = this.querySelector(`script${selector}`);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  #appendMessage(role, text) {
    const wrapper = document.createElement('div');
    wrapper.className = `ai-chat-msg ai-chat-msg--${role}`;

    if (role === 'assistant') {
      const avatar = document.createElement('div');
      avatar.className = 'ai-chat-msg__avatar';
      avatar.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0283/7984/0586/files/compass-black-red_3f86301c-110d-468f-8baa-aba20dc9e862.png?v=1767804967" alt="Cancha" width="20" height="20" style="border-radius:50%;object-fit:contain;">';
      wrapper.appendChild(avatar);
    }

    const bubble = document.createElement('div');
    bubble.className = `ai-chat-bubble ai-chat-bubble--${role}`;
    bubble.textContent = text;
    wrapper.appendChild(bubble);

    this.messagesEl?.appendChild(wrapper);
    this.#scrollToBottom();
  }

  async #addToCart(btn, variantId, source = 'chat') {
    if (!variantId || btn.disabled) return;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      // Collect section IDs the theme needs to re-render (e.g. cart drawer)
      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));

      // Use FormData like the theme does so Shopify returns rendered sections
      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', '1');
      if (sectionsToBundle.size > 0) {
        formData.set('sections', [...sectionsToBundle].join(','));
      }

      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (!addResponse.ok) throw new Error('Add to cart failed');

      const responseJson = await addResponse.json();

      btn.textContent = 'Added!';
      btn.classList.add('ai-chat-atc__btn--added');
      this.#track('chat_add_to_cart', { product_id: this.productId, variant_id: variantId });

      // Tag the cart with attribution source for order webhook tracking
      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: { '_cancha_source': source } }),
      }).catch(() => {});

      // Track conversion server-side
      fetch(this.baseUrl + '/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'add_to_cart',
          source,
          session_id: this.conversationHistory?.[0]?.session_id || null,
          product_id: this.productId,
          variant_id: String(variantId),
        }),
      }).catch(() => {});

      // Close the AI chat drawer
      const chatDrawer = document.querySelector('x-drawer.ai-chat-drawer[open]');
      if (chatDrawer) {
        try {
          await chatDrawer.hide();
        } catch {
          chatDrawer.removeAttribute('open');
          chatDrawer.style.display = 'none';
        }
      }

      // Build cart object with sections, then dispatch cart:change to trigger theme's cart drawer
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections || {};

      // Fallback: if no sections returned, fetch cart drawer section HTML manually
      if (Object.keys(cartContent.sections).length === 0) {
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) {
          const sectionEl = drawerEl.closest('.shopify-section');
          const sectionId = sectionEl?.id?.replace('shopify-section-', '');
          if (sectionId) {
            cartContent.sections[sectionId] = await (await fetch(`${Shopify.routes.root}?section_id=${sectionId}`)).text();
          }
        }
      }

      const items = responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson];
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent, items },
      }));
    } catch (err) {
      console.error('ATC error:', err);
      btn.textContent = 'Error';
      setTimeout(() => {
        btn.textContent = 'Add to Cart';
        btn.disabled = false;
      }, 2000);
    }
  }

  #appendError() {
    const el = document.createElement('div');
    el.className = 'ai-chat-msg ai-chat-msg--error';
    el.textContent = 'Something went wrong. Please try again.';
    this.messagesEl?.appendChild(el);
    this.#scrollToBottom();
  }

  #showTypingIndicator() {
    const wrapper = document.createElement('div');
    wrapper.className = 'ai-chat-msg ai-chat-msg--assistant';

    const avatar = document.createElement('div');
    avatar.className = 'ai-chat-msg__avatar';
    avatar.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0283/7984/0586/files/compass-black-red_3f86301c-110d-468f-8baa-aba20dc9e862.png?v=1767804967" alt="Cancha" width="20" height="20" style="border-radius:50%;object-fit:contain;">';
    wrapper.appendChild(avatar);

    const typing = document.createElement('div');
    typing.className = 'ai-chat-typing';
    typing.innerHTML = '<span></span><span></span><span></span>';
    wrapper.appendChild(typing);

    this.messagesEl?.appendChild(wrapper);
    this.#scrollToBottom();
    return wrapper;
  }

  #showSuggestions() {
    if (!this.suggestionsEl) return;
    // Hide pills for already-asked questions
    this.suggestionsEl.querySelectorAll('[data-suggestion]').forEach((pill) => {
      pill.hidden = this.#askedQuestions.has(pill.dataset.suggestion);
    });
    this.suggestionsEl.hidden = false;
  }

  #hideSuggestions() {
    if (this.suggestionsEl) this.suggestionsEl.hidden = true;
  }

  #scrollToBottom() {
    if (this.messagesEl) {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }
  }

  #setSubmitDisabled(disabled) {
    if (this.submitBtn) this.submitBtn.disabled = disabled;
  }

  #track(event, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  }
}

if (!window.customElements.get('ai-chat-block')) {
  window.customElements.define('ai-chat-block', AiChatBlock);
}
if (!window.customElements.get('ai-chat-widget')) {
  window.customElements.define('ai-chat-widget', AiChatWidget);
}
